import type {
  Canal,
  Negocio,
  NivelInteres,
  Origen,
  ResultadoContacto,
  ResultadoPresentacion,
} from "./tipos";

/** Todos los orígenes (filtros, Inicio). `prospect` lo pone Nevora Prospect (S1); `web`, el formulario (35). */
export const ORIGENES: Origen[] = [
  "puerta_fria",
  "extraccion",
  "propio_comercial",
  "prospect",
  "web",
];

/** Orígenes que se eligen a mano en el alta y la ficha: `prospect` solo lo pone Prospect. */
export const ORIGENES_ALTA: Origen[] = [
  "puerta_fria",
  "extraccion",
  "propio_comercial",
];

export const CANALES: Canal[] = [
  "llamada",
  "visita",
  "email",
  "whatsapp",
  "videollamada",
  "otro",
];

/** Resultados de los botones «He llamado / He visitado / He escrito». */
export const RESULTADOS_CONTACTO: ResultadoContacto[] = [
  "quiere_reunion",
  "interesado",
  "ni_si_ni_no",
  "no_contesta",
  "no_interesa",
  "mas_adelante",
];

/**
 * Resultados de los botones para esta ficha. «Interesado» (28c) y «No quiere que le contactemos»
 * (36) solo si la base ya los tiene (la ficha trae `interesado_at` y `no_shows`). «No se presentó»
 * ya no está aquí (39, Robert 23/10/2026): lo marca quien hace la consultoría en el recuadro de la
 * consultoría, como resultado de la consultoría y no como llamada.
 */
export const resultadosDeContacto = (
  negocio: Pick<Negocio, "etapa">,
): ResultadoContacto[] => {
  const con36 = "no_shows" in negocio;
  return [
    ...RESULTADOS_CONTACTO.filter(
      (r) => r !== "interesado" || "interesado_at" in negocio,
    ),
    ...(con36 ? (["no_quiere_contacto"] as const) : []),
  ];
};

/** Resultados de la hoja «Resultado de la presentación» (Robert, 02/10/2026). */
export const RESULTADOS_PRESENTACION: ResultadoPresentacion[] = [
  "aceptada",
  "lo_pensara",
  "repropuesta",
  "no_interesa",
];

export const claveOrigen = (origen: Origen) => `nevora.origenes.${origen}`;
export const claveCanal = (canal: Canal) => `nevora.canales.${canal}`;
export const claveResultado = (r: string) => `nevora.resultados.${r}`;

/** Canales del botón «He escrito» (v1.1): la hoja deja elegir entre los dos. */
export const CANALES_ESCRITOS: Canal[] = ["whatsapp", "email"];
export const esCanalEscrito = (canal: Canal) =>
  CANALES_ESCRITOS.includes(canal);

/**
 * Etiqueta del resultado según el canal: visita, escrito y la presentación (videollamada)
 * tienen textos propios.
 */
export const claveResultadoPorCanal = (canal: Canal, r: string) =>
  canal === "videollamada"
    ? `nevora.resultados_presentacion.${r}`
    : canal === "visita"
      ? `nevora.resultados_visita.${r}`
      : esCanalEscrito(canal)
        ? `nevora.resultados_escrito.${r}`
        : claveResultado(r);

export const claveAyudaResultadoPorCanal = (canal: Canal, r: string) =>
  canal === "videollamada"
    ? `nevora.resultados_presentacion_ayuda.${r}`
    : canal === "visita"
      ? `nevora.resultados_visita_ayuda.${r}`
      : esCanalEscrito(canal)
        ? `nevora.resultados_escrito_ayuda.${r}`
        : `nevora.resultados_ayuda.${r}`;

/** Niveles de interés (28d), de más a menos: así se ordenan las columnas del Pipeline. */
export const NIVELES_INTERES: NivelInteres[] = ["caliente", "tibio", "frio"];

/** Posición para ordenar: caliente, tibio, frío y, al final, sin nivel. */
export const ordenInteres = (n: NivelInteres | null | undefined): number =>
  n ? NIVELES_INTERES.indexOf(n) : NIVELES_INTERES.length;

/** ¿La base ya tiene el nivel (28d)? La ficha trae la columna, aunque esté vacía. */
export const conInteres = (negocio: object) => "interes" in negocio;
